import React, { useEffect, useMemo, useState } from 'react';
import { Card, Button, Badge, DataTable, Input, Modal } from '../../../components/ui';
import { Plus, User, RefreshCw, KeyRound, Power, Trash2 } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';

interface LimiteInfo {
  max_vendedores: number;
  usados: number;
  disponibles: number;
}

interface VendedorItem {
  id: string;
  username: string;
  nombre: string;
  role: 'VENDEDOR_CON_CAJA' | 'VENDEDOR_SIN_CAJA';
  activo: boolean;
}

interface CrearVendedorForm {
  nombre: string;
  username: string;
  password: string;
  confirmPassword: string;
  role: 'VENDEDOR_CON_CAJA' | 'VENDEDOR_SIN_CAJA';
}

const emptyForm: CrearVendedorForm = {
  nombre: '',
  username: '',
  password: '',
  confirmPassword: '',
  role: 'VENDEDOR_CON_CAJA',
};

const LOCAL_FILTRO_STORAGE_KEY = 'local_vendedores_filtro_estado';

export const VendedoresPage: React.FC = () => {
  const [vendedores, setVendedores] = useState<VendedorItem[]>([]);
  const [limite, setLimite] = useState<LimiteInfo>({ max_vendedores: 0, usados: 0, disponibles: 0 });
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState<CrearVendedorForm>(emptyForm);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [selectedVendedor, setSelectedVendedor] = useState<VendedorItem | null>(null);
  const [filtroEstado, setFiltroEstado] = useState<'TODOS' | 'ACTIVOS' | 'INACTIVOS'>(() => {
    const saved = localStorage.getItem(LOCAL_FILTRO_STORAGE_KEY);
    return saved === 'ACTIVOS' || saved === 'INACTIVOS' ? saved : 'TODOS';
  });

  const cargarDatos = async () => {
    setLoading(true);
    setError('');
    try {
      const [vendedoresData, limiteData] = await Promise.all([
        fetchAPI<VendedorItem[]>('/usuarios/local/vendedores'),
        fetchAPI<LimiteInfo>('/usuarios/local/vendedores/limite'),
      ]);
      setVendedores(vendedoresData);
      setLimite(limiteData);
    } catch (e: any) {
      setError(e.message || 'No se pudieron cargar vendedores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarDatos();
  }, []);

  useEffect(() => {
    localStorage.setItem(LOCAL_FILTRO_STORAGE_KEY, filtroEstado);
  }, [filtroEstado]);

  const conteoCaja = useMemo(() => vendedores.filter((v) => v.role === 'VENDEDOR_CON_CAJA').length, [vendedores]);
  const conteoSinCaja = useMemo(() => vendedores.filter((v) => v.role === 'VENDEDOR_SIN_CAJA').length, [vendedores]);
  const vendedoresFiltrados = useMemo(() => {
    if (filtroEstado === 'ACTIVOS') return vendedores.filter((v) => v.activo);
    if (filtroEstado === 'INACTIVOS') return vendedores.filter((v) => !v.activo);
    return vendedores;
  }, [vendedores, filtroEstado]);

  const onCrearVendedor = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (form.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setSaving(true);
    try {
      await fetchAPI<VendedorItem>('/usuarios/local/vendedores', {
        method: 'POST',
        body: JSON.stringify({
          nombre: form.nombre,
          username: form.username,
          password: form.password,
          role: form.role,
          local_id: 'IGNORED_BY_BACKEND',
        }),
      });
      setModalOpen(false);
      setForm(emptyForm);
      await cargarDatos();
    } catch (e: any) {
      setError(e.message || 'No se pudo crear el vendedor');
    } finally {
      setSaving(false);
    }
  };

  const abrirModalPassword = (vendedor: VendedorItem) => {
    setSelectedVendedor(vendedor);
    setPassword('');
    setConfirmPassword('');
    setPasswordModalOpen(true);
    setError('');
  };

  const cambiarPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedVendedor) return;

    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setSaving(true);
    setError('');
    try {
      await fetchAPI<VendedorItem>(`/usuarios/local/vendedores/${selectedVendedor.id}/password`, {
        method: 'PATCH',
        body: JSON.stringify({ password }),
      });
      setPasswordModalOpen(false);
    } catch (e: any) {
      setError(e.message || 'No se pudo cambiar la contraseña');
    } finally {
      setSaving(false);
    }
  };

  const toggleActivo = async (vendedor: VendedorItem) => {
    setActionLoadingId(vendedor.id);
    setError('');
    try {
      await fetchAPI<VendedorItem>(`/usuarios/local/vendedores/${vendedor.id}/estado`, {
        method: 'PATCH',
        body: JSON.stringify({ activo: !vendedor.activo }),
      });
      await cargarDatos();
    } catch (e: any) {
      setError(e.message || 'No se pudo actualizar estado');
    } finally {
      setActionLoadingId(null);
    }
  };

  const eliminarVendedor = async (vendedor: VendedorItem) => {
    const ok = window.confirm(`¿Eliminar vendedor ${vendedor.nombre}? Se conservará el historial de ventas.`);
    if (!ok) return;

    setActionLoadingId(vendedor.id);
    setError('');
    try {
      await fetchAPI<VendedorItem>(`/usuarios/local/vendedores/${vendedor.id}`, { method: 'DELETE' });
      await cargarDatos();
    } catch (e: any) {
      setError(e.message || 'No se pudo eliminar el vendedor');
    } finally {
      setActionLoadingId(null);
    }
  };

  const columns = [
    {
      key: 'nombre',
      header: 'Vendedor',
      render: (u: VendedorItem) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-gray-500" />
          </div>
          <div>
            <p className="font-medium text-gray-900">{u.nombre}</p>
            <p className="text-sm text-gray-500">@{u.username}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Tipo',
      render: (u: VendedorItem) => (
        <Badge variant={u.role === 'VENDEDOR_CON_CAJA' ? 'success' : 'warning'}>
          {u.role === 'VENDEDOR_CON_CAJA' ? 'CON CAJA' : 'SIN CAJA'}
        </Badge>
      ),
    },
    {
      key: 'estado',
      header: 'Estado',
      render: (u: VendedorItem) => (
        <Badge variant={u.activo ? 'success' : 'neutral'}>{u.activo ? 'ACTIVO' : 'INACTIVO'}</Badge>
      ),
    },
    {
      key: 'acciones',
      header: 'Acciones',
      render: (u: VendedorItem) => (
        <div className="flex items-center gap-2">
          <button
            type="button"
            title="Cambiar contraseña"
            onClick={() => abrirModalPassword(u)}
            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
            disabled={actionLoadingId === u.id}
          >
            <KeyRound size={16} />
          </button>
          <button
            type="button"
            title={u.activo ? 'Desactivar' : 'Activar'}
            onClick={() => toggleActivo(u)}
            className="p-2 text-gray-500 hover:text-warning-600 hover:bg-warning-50 rounded-lg"
            disabled={actionLoadingId === u.id}
          >
            <Power size={16} />
          </button>
          <button
            type="button"
            title="Eliminar (conservar historial)"
            onClick={() => eliminarVendedor(u)}
            className="p-2 text-gray-500 hover:text-danger-600 hover:bg-danger-50 rounded-lg"
            disabled={actionLoadingId === u.id}
          >
            <Trash2 size={16} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vendedores</h1>
          <p className="text-gray-500">Crear y administrar vendedores del local</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value as 'TODOS' | 'ACTIVOS' | 'INACTIVOS')}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            title="Filtrar estado"
          >
            <option value="TODOS">Todos</option>
            <option value="ACTIVOS">Activos</option>
            <option value="INACTIVOS">Inactivos</option>
          </select>
          <button
            onClick={cargarDatos}
            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
            title="Refrescar"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
          <Button
            variant="primary"
            leftIcon={<Plus size={18} />}
            onClick={() => setModalOpen(true)}
            disabled={limite.disponibles <= 0}
          >
            Nuevo Vendedor
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <p className="text-sm text-gray-500">Límite ADMIN</p>
          <p className="text-2xl font-bold">{limite.max_vendedores}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">Vendedores activos</p>
          <p className="text-2xl font-bold text-primary-600">{limite.usados}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">Con Caja</p>
          <p className="text-2xl font-bold text-success-600">{conteoCaja}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">Sin Caja</p>
          <p className="text-2xl font-bold text-warning-600">{conteoSinCaja}</p>
        </Card>
      </div>

      {error && <p className="text-sm text-danger-600">{error}</p>}

      <Card padding="none">
        <DataTable
          columns={columns}
          data={vendedoresFiltrados}
          keyExtractor={(u: VendedorItem) => u.id}
          isLoading={loading}
          emptyMessage="No hay vendedores en este local"
        />
      </Card>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Crear vendedor con usuario y contraseña"
        size="lg"
      >
        <form onSubmit={onCrearVendedor} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              label="Nombre"
              value={form.nombre}
              onChange={(e) => setForm((p) => ({ ...p, nombre: e.target.value }))}
              required
            />
            <Input
              label="Username"
              value={form.username}
              onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))}
              required
            />
            <Input
              label="Contraseña"
              type="password"
              value={form.password}
              onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
              required
            />
            <Input
              label="Confirmar contraseña"
              type="password"
              value={form.confirmPassword}
              onChange={(e) => setForm((p) => ({ ...p, confirmPassword: e.target.value }))}
              required
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
              <select
                value={form.role}
                onChange={(e) => setForm((p) => ({ ...p, role: e.target.value as CrearVendedorForm['role'] }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="VENDEDOR_CON_CAJA">Vendedor con caja</option>
                <option value="VENDEDOR_SIN_CAJA">Vendedor sin caja</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button type="submit" variant="primary" isLoading={saving}>Crear usuario</Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
        title={`Cambiar contraseña${selectedVendedor ? `: ${selectedVendedor.nombre}` : ''}`}
        size="md"
      >
        <form onSubmit={cambiarPassword} className="space-y-4">
          <Input
            label="Nueva contraseña"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <Input
            label="Confirmar contraseña"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setPasswordModalOpen(false)}>Cancelar</Button>
            <Button type="submit" variant="primary" isLoading={saving}>Guardar contraseña</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default VendedoresPage;
